import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { DELETE_URL } from '@utils/api/endpoints.ts';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { BaseError } from '@utils/types/shared/base.ts';

export const useDeleteFolder = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const deleteFolders = async (folderIds: number[]) => {
    if (folderIds.length !== 1) {
      throw new Error(t('delete-folders-one-at-a-time'));
    }
    for (const folderId of folderIds) {
      try {
        await Axios.delete(DELETE_URL(folderId.toString()).deleteFolder);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        // Ignore 404 errors - folder already deleted
        if (error?.response?.status === 404) {
          continue;
        }
        // Re-throw other errors
        throw error;
      }
    }
  };

  return useMutation<void, AxiosError<BaseError>, number[]>(
    (folderIds: number[]) => deleteFolders(folderIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('folders');
        queryClient.invalidateQueries('folder');
        toast.success(t('folders-deleted-successfully'));
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        if (error?.response?.status === 403) {
          queryClient.invalidateQueries('folders');
          queryClient.invalidateQueries('folder');
          toast.error(t('folder-delete-permission-denied'));
          return;
        }
        // Don't show error for 404 - folder already deleted
        if (error?.response?.status === 404) {
          // Silently succeed - folder was already deleted
          queryClient.invalidateQueries('folders');
          queryClient.invalidateQueries('folder');
          toast.success(t('folders-deleted-successfully'));
          return;
        }

        let messages: string[] = [];

        if (Array.isArray(error?.response?.data?.detail)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          messages = error.response.data.detail.map((err: any) => {
            const field = err.loc?.[1] ?? 'field';
            return `${field}: ${err.msg}`;
          });
        } else if (error?.response?.data?.message) {
          messages = [error.response.data.message];
        } else if (error?.response?.data?.error) {
          messages = [error.response.data.error];
        } else if (error?.message) {
          messages = [error.message];
        } else {
          messages = [t('an-error-occurred-while-deleting-the-folder')];
        }

        toast.error(messages.join('\n'));
      },
      useErrorBoundary: false
    }
  );
};
